import {
  InterviewReminderKindSchema,
  InterviewReminderRecipientRoleSchema,
} from "@workspace/dto/interview/reminder";
import { z } from "zod";
import { buildFromAddress } from "../utils/build-email-address";
import { defineEmailNotificationHandler } from "./types";

const InterviewReminderSchema = z.object({
  candidateName: z.string(),
  conversationId: z.string(),
  interviewDate: z.string(),
  interviewId: z.string(),
  interviewTitle: z.string(),
  jobTitle: z.string(),
  kind: InterviewReminderKindSchema,
  organizationName: z.string(),
  recipientRole: InterviewReminderRecipientRoleSchema,
});

function getReminderTitle(metadata: Record<string, unknown>): string {
  if (metadata.kind === "response_nudge") {
    return metadata.recipientRole === "employee"
      ? "Respond to your interview invite"
      : "Candidate response pending";
  }
  return metadata.kind === "hour_before"
    ? "Interview starts soon"
    : "Interview tomorrow";
}

function getReminderDescription(metadata: Record<string, unknown>): string {
  const candidateName = String(metadata.candidateName);
  const interviewTitle = String(metadata.interviewTitle);
  const organizationName = String(metadata.organizationName);

  if (metadata.kind === "response_nudge") {
    return metadata.recipientRole === "employee"
      ? `${organizationName} is waiting for your response to ${interviewTitle}.`
      : `${candidateName} has not responded to ${interviewTitle} yet.`;
  }

  const otherParty =
    metadata.recipientRole === "employee" ? organizationName : candidateName;
  return `${interviewTitle} with ${otherParty} is coming up.`;
}

export const interviewReminderHandler = defineEmailNotificationHandler({
  type: "interview.reminder",
  icon: "interview",
  title: getReminderTitle,
  description: getReminderDescription,
  schema: InterviewReminderSchema,
  buildNotificationPayload(data, _ctx) {
    return {
      metadata: {
        candidateName: data.candidateName,
        conversationId: data.conversationId,
        interviewDate: data.interviewDate,
        interviewId: data.interviewId,
        interviewTitle: data.interviewTitle,
        jobTitle: data.jobTitle,
        kind: data.kind,
        organizationName: data.organizationName,
        recipientRole: data.recipientRole,
      },
      links: {
        href: `/conversations/${data.conversationId}`,
        label:
          data.kind === "response_nudge" ? "Respond now" : "View interview",
      },
      priority: data.kind === "hour_before" ? 7 : 5,
      groupId: `interview-reminder:${data.interviewId}:${data.kind}`,
    };
  },
  buildEmailPayload(data, recipient, { baseUrl }) {
    return {
      name: "email/interview.reminder",
      data: {
        to: recipient.email ?? "",
        from: buildFromAddress(),
        recipientName: recipient.name ?? "there",
        recipientRole: data.recipientRole,
        kind: data.kind,
        interviewTitle: data.interviewTitle,
        interviewDate: data.interviewDate,
        candidateName: data.candidateName,
        organizationName: data.organizationName,
        jobTitle: data.jobTitle,
        conversationUrl: `${baseUrl}/conversations/${data.conversationId}`,
      },
    };
  },
});
